import { adminGuard, emailVerifiedGuard, kycFormGuard } from 'auth';
import { AppointmentsPageComponent, MyCardsPageComponent, UserTreePageComponent } from 'credo';
import { UsersPageComponent } from './admin/containers/users-page/users-page.component';
import { XshopAdminPageComponent } from './admin/containers/xshop-admin-page/xshop-admin-page.component';
import { BetaActivationPageComponent } from './containers/pages/beta-activation-page/beta-activation-page.component';
import { BookingsPageComponent } from './containers/pages/bookings-page/bookings-page.component';
import { DeleteAccountPageComponent } from './containers/pages/delete-account-page/delete-account-page.component';
import { EarningsStatisticsPageComponent } from './containers/pages/earnings-statistics-page/earnings-statistics-page.component';
import { FunnelCheckEmailPageComponent } from './containers/pages/funnel-check-email-page/funnel-check-email-page.component';
import { FunnelLandingPageComponent } from './containers/pages/funnel-landing-page/funnel-landing-page.component';
import { FunnelQuizPageComponent } from './containers/pages/funnel-quiz-page/funnel-quiz-page.component';
import { FunnelQuizStartPageComponent } from './containers/pages/funnel-quiz-start-page/funnel-quiz-start-page.component';
import { FunnelResultPageComponent } from './containers/pages/funnel-result-page/funnel-result-page.component';
import { FunnelStoryPageComponent } from './containers/pages/funnel-story-page/funnel-story-page.component';
import { KycConfirmedPageComponent } from './containers/pages/kyc-confirmed-page/kyc-confirmed-page.component';
import { KycFormPageComponent } from './containers/pages/kyc-form-page/kyc-form-page.component';
import { KycSumsubPageComponent } from './containers/pages/kyc-sumsub-page/kyc-sumsub-page.component';
import { KycTokenPageComponent } from './containers/pages/kyc-token-page/kyc-token-page.component';
import { MainPageComponent } from './containers/pages/main-page/main-page.component';
import { UserPayoutRequestPageComponent } from './containers/pages/user-payout-request-page/user-payout-request-page.component';
import { UserPayoutsPageComponent } from './containers/pages/user-payouts-page/user-payouts-page.component';
import { UserStatisticsPageComponent } from './containers/pages/user-statistics-page/user-statistics-page.component';

import { routeBuilder } from 'credo';

export const routes = routeBuilder()
  .main(MainPageComponent)
  .withMaintenance()
  .withLegalAcknowledgement()
  .withCommunity()
  // Config domain 4 publishes its policy documents as terms / privacy /
  // imprint (see data/media/4/en/policy/). The login page's built-in fallback
  // asks for the fac3less names (terms_of_service / privacy_policy /
  // legal_notice), which do not exist here — every link came back 500
  // ActionView::MissingTemplate.
  .withLoginActions([
    { labelKey: 'policy.privacy',     policy: 'privacy' },
    { labelKey: 'policy.tos',         policy: 'terms' },
    { labelKey: 'policy.legalNotice', policy: 'imprint' }
  ])
  .root(
    {
      path: 'enter-b-e-t-a-test',
      redirectTo: () => {
        const lang = localStorage.getItem('language') || 'en';
        return `/${lang}/enter-b-e-t-a-test`;
      }
    },
    // WolfPack Distribution story funnel — the public referral URLs
    // (`referral_url_wpd` = https://wolfpack-distribution.com/r/<userId>).
    //
    // These are deliberately NOT language-prefixed: the marketing link is shared as-is and the
    // page picks the language itself (from /r/:lang/… when given, otherwise the stored/browser
    // preference) and offers a switcher. `/r/:lang/:sponsorId` and `/r/:sponsorId` differ only in
    // segment count, and `/r/de` alone is disambiguated inside the component — sponsor ids are
    // always numeric (Funnel::SponsorResolver), so a segment that names a configured language
    // cannot be one.
    //
    // The lead-capture page these hand off to stays at `/:lang/r/:sponsorId` (below).
    { path: 'r',                    component: FunnelStoryPageComponent, pathMatch: 'full', data: { animation: 'funnel-story' } },
    { path: 'r/:sponsorId',         component: FunnelStoryPageComponent, data: { animation: 'funnel-story' } },
    { path: 'r/:lang/:sponsorId',   component: FunnelStoryPageComponent, data: { animation: 'funnel-story' } }
  )
  .lang.public(
    { path: 'kyc/confirm/:token', component: KycTokenPageComponent, data: { action: 'confirm', animation: 'kyc-confirm' } },
    { path: 'kyc/correct/:token', component: KycTokenPageComponent, data: { action: 'correct', animation: 'kyc-correct' } },
    // FAC3LESS funnel pages (public — no auth required)
    { path: 'r',                 component: FunnelLandingPageComponent,    data: { animation: 'funnel-landing' } },
    { path: 'r/:sponsorId',      component: FunnelLandingPageComponent,    data: { animation: 'funnel-landing' } },
    { path: 'quiz/check-email',  component: FunnelCheckEmailPageComponent, data: { animation: 'funnel-check-email' } },
    { path: 'quiz/start',        component: FunnelQuizStartPageComponent,  data: { animation: 'funnel-quiz-start' } },
    { path: 'quiz',              component: FunnelQuizPageComponent,       data: { animation: 'funnel-quiz' } },
    // WolfPack wizard ends by setting a password (no purchase) — the
    // checkout/payment/success funnel steps are intentionally not routed.
    { path: 'quiz/result',       component: FunnelResultPageComponent,     data: { animation: 'funnel-result' } }
  )
  // .replace('main', {
  //   path: 'main',
  //   component: MainPageComponent,
  //   canActivate: [emailVerifiedGuard],
  //   data: { title: 'Dashboard', animation: 'main' }
  // })
  .protected(
    {
      path: 'users',
      component: UsersPageComponent,
      data: { title: 'Users', animation: 'users' }
    },
    {
      path: 'user-statistics',
      component: UserStatisticsPageComponent,
      data: { title: 'User Statistics', animation: 'user-statistics' }
    },
    {
      path: 'earnings-statistics',
      component: EarningsStatisticsPageComponent,
      data: { title: 'Earnings Statistics', animation: 'earnings-statistics' }
    },
    {
      path: 'user-tree',
      component: UserTreePageComponent,
      data: { title: 'User Tree', animation: 'user-tree' }
    },
    {
      path: 'user-tree/:userId',
      component: UserTreePageComponent,
      data: { title: 'User Tree', animation: 'user-tree' }
    },
    {
      path: 'bookings',
      component: BookingsPageComponent,
      data: { title: 'Bookings', animation: 'bookings' }
    },
    // Card partner programme, member side. Same page and same backend answer as on
    // fac3less and mnlo: this app shares the fac3less tenant, and the entitlement
    // is user-wide.
    {
      path: 'mycards',
      component: MyCardsPageComponent,
      canActivate: [emailVerifiedGuard],
      data: { title: 'My Cards', animation: 'mycards' }
    },
    // Appointments ("Termine") — the brand's published call schedule. Member
    // facing; the admin CRUD lives under /admin/appointments.
    {
      path: 'appointments',
      component: AppointmentsPageComponent,
      canActivate: [emailVerifiedGuard],
      data: { title: 'Appointments', animation: 'appointments' }
    },
    {
      path: 'my-payouts',
      component: UserPayoutsPageComponent,
      data: { title: 'My Payouts', animation: 'user-payouts' }
    },
    {
      path: 'my-payouts/request',
      component: UserPayoutRequestPageComponent,
      data: { title: 'Request Payout', animation: 'user-payout-request' }
    },
    {
      path: 'delete-account',
      component: DeleteAccountPageComponent,
      data: { title: 'Delete Account', animation: 'delete-account' }
    },
    {
      path: 'enter-b-e-t-a-test',
      component: BetaActivationPageComponent,
      data: { title: 'Beta Program', animation: 'beta-activation' }
    },
    {
      path: 'kyc',
      component: KycFormPageComponent,
      canActivate: [kycFormGuard],
      data: { title: 'KYC Verification', animation: 'kyc' }
    },
    {
      path: 'kyc/confirmed',
      component: KycConfirmedPageComponent,
      data: { title: 'KYC Confirmed', animation: 'kyc-confirmed' }
    },
    {
      path: 'kyc/sumsub',
      component: KycSumsubPageComponent,
      data: { title: 'KYC Verification', animation: 'kyc-sumsub' }
    },
    {
      path: 'xshop-admin',
      component: XshopAdminPageComponent,
      data: { title: 'XShop Admin', animation: 'xshop-admin' }
    },
    {
      path: 'admin',
      canMatch: [adminGuard],
      canActivate: [adminGuard],
      loadChildren: () =>
        import('./admin/admin.routes')
          .then(routes => routes.ADMIN_ROUTES)
    }
  )
  .build();

  